/**
 * Voice chat for the meeting transcript (2026-10-06, the user's request): every client cuts
 * its own microphone into utterances and the server's Whisper writes them into the meeting's
 * transcript under the speaker's name (`POST /api/speech`). Only the speaker's own microphone
 * is sent, never what they hear; the audio goes to the ClinSpaceXR server only.
 *
 * It pauses while the speaker is muted, while they are talking to Luma (the question is
 * logged as a question) and while Luma's voice plays here (so her words, heard through the
 * speakers, are not written down as the speaker's).
 */
import { api } from '../lib/api.ts';
import { levelOf, resample } from '../assistant/voice.ts';

/** Utterance detection for continuous speech, pure so it is unit tested. Times in ms. */
export const CONTINUOUS_VAD = {
  /** An utterance ends after this much quiet. */
  silenceMs: 800,
  /** Long speech is cut into pieces of at most this long. */
  maxMs: 25_000,
  /** Shorter than this is a cough or a click, not words. */
  minSpeechMs: 450,
  /** Speech is louder than the room's noise by this factor, and never quieter than minLevel. */
  factor: 3,
  minLevel: 0.012,
  /** The noise floor follows the room up this slowly (per second, as a factor). */
  floorRise: 1.15,
  /** Over this long, speech rises and falls; a steadier sound is noise (a fan, a hum). */
  steadyMs: 3000,
  steadyRatio: 0.5,
  prerollMs: 300,
};

export type UtteranceEvent = 'start' | 'end' | 'cut' | 'noise' | undefined;

/** Feeds chunk loudness: an utterance starts, ends after a pause, or is cut when too long. */
export class UtteranceDetector {
  private floor = 0.004;
  private quiet = 0;
  private length = 0;
  private loud = 0;
  /** Recent chunk levels while speaking, to tell speech from a steady hum. */
  private recent: { level: number; ms: number }[] = [];
  speaking = false;

  get threshold(): number {
    return Math.max(CONTINUOUS_VAD.minLevel, this.floor * CONTINUOUS_VAD.factor);
  }

  /** Milliseconds of loud chunks in the utterance (or piece) that just ended. */
  completedMs = 0;

  /** Drops the utterance in progress (paused mid-sentence). */
  reset(): void {
    this.speaking = false;
    this.quiet = 0;
    this.length = 0;
    this.loud = 0;
    this.recent = [];
  }

  push(level: number, ms: number): UtteranceEvent {
    if (!this.speaking) {
      // The floor drops at once to quieter chunks and rises slowly with a louder room.
      this.floor = Math.min(
        Math.max(level, 0.001),
        this.floor * CONTINUOUS_VAD.floorRise ** (ms / 1000),
      );
      if (level > this.threshold) {
        this.speaking = true;
        this.quiet = 0;
        this.length = 0;
        this.loud = 0;
        this.recent = [];
        return 'start';
      }
      return undefined;
    }
    this.length += ms;
    this.recent.push({ level, ms });
    let span = this.recent.reduce((n, r) => n + r.ms, 0);
    while (span - this.recent[0]!.ms >= CONTINUOUS_VAD.steadyMs) span -= this.recent.shift()!.ms;
    if (span >= CONTINUOUS_VAD.steadyMs) {
      const levels = this.recent.map((r) => r.level);
      const low = Math.min(...levels);
      if (low / Math.max(...levels) > CONTINUOUS_VAD.steadyRatio) {
        // A steady sound, not speech: it becomes the room's noise floor.
        this.floor = low;
        this.reset();
        return 'noise';
      }
    }
    if (level > this.threshold * 0.8) {
      this.quiet = 0;
      this.loud += ms;
    } else this.quiet += ms;
    if (this.quiet >= CONTINUOUS_VAD.silenceMs) {
      this.speaking = false;
      this.completedMs = this.loud;
      return 'end';
    }
    if (this.length >= CONTINUOUS_VAD.maxMs) {
      // Carry straight on into the next piece.
      this.completedMs = this.loud;
      this.length = 0;
      this.loud = 0;
      return 'cut';
    }
    return undefined;
  }
}

export interface TranscriberDeps {
  microphone(): { stream: MediaStream; context: AudioContext } | undefined;
  sessionId(): string | undefined;
  /** True while nothing should be written down (muted, talking to Luma, Luma speaking). */
  paused(): boolean;
}

export class SpeechTranscriber {
  private source: MediaStreamAudioSourceNode | undefined;
  /** The microphone stream listened to (a new one after the mic is reopened). */
  private bound: MediaStream | undefined;
  private processor: ScriptProcessorNode | undefined;
  private sink: GainNode | undefined;
  private readonly detector = new UtteranceDetector();
  private chunks: Float32Array[] = [];
  private preroll: Float32Array[] = [];
  private startedAt = 0;
  private stopped = false;
  private inFlight = 0;
  /** Dev hooks: utterances sent, and the last error. */
  sent = 0;
  lastError: string | undefined;

  constructor(private readonly deps: TranscriberDeps) {}

  get running(): boolean {
    return !!this.processor;
  }

  /** Starts listening once the microphone is there (again, if it was reopened); every frame. */
  update(): void {
    if (this.stopped) return;
    const mic = this.deps.microphone();
    if (this.processor && mic?.stream === this.bound) return;
    this.detach();
    if (!mic) return;
    this.bound = mic.stream;
    const ctx = mic.context;
    this.source = ctx.createMediaStreamSource(mic.stream);
    // ScriptProcessor is deprecated but runs everywhere the headset does, without a module.
    this.processor = ctx.createScriptProcessor(4096, 1, 1);
    this.sink = ctx.createGain();
    this.sink.gain.value = 0;
    this.source.connect(this.processor);
    this.processor.connect(this.sink);
    this.sink.connect(ctx.destination);
    const rate = ctx.sampleRate;
    const prerollChunks = Math.ceil((CONTINUOUS_VAD.prerollMs / 1000) * (rate / 4096));
    this.processor.onaudioprocess = (e) => {
      const chunk = new Float32Array(e.inputBuffer.getChannelData(0));
      if (this.deps.paused()) {
        if (this.detector.speaking) {
          this.detector.reset();
          this.finish(rate, false);
        }
        this.preroll = [];
        return;
      }
      const ms = (chunk.length / rate) * 1000;
      const event = this.detector.push(levelOf(chunk), ms);
      if (event === 'noise') {
        // A steady hum, not speech: nothing of it is sent.
        this.finish(rate, false);
      } else if (event === 'start') {
        this.startedAt = Date.now() - this.preroll.length * ms;
        this.chunks = [...this.preroll, chunk];
        this.preroll = [];
      } else if (this.detector.speaking || event === 'end') {
        this.chunks.push(chunk);
        if (event === 'end') this.finish(rate, true);
        else if (event === 'cut') {
          this.finish(rate, true);
          this.startedAt = Date.now();
        }
      } else {
        this.preroll.push(chunk);
        if (this.preroll.length > prerollChunks) this.preroll.shift();
      }
    };
  }

  private finish(rate: number, keep: boolean): void {
    const chunks = this.chunks;
    const speechMs = this.detector.completedMs;
    this.chunks = [];
    if (!keep || speechMs < CONTINUOUS_VAD.minSpeechMs || !chunks.length) return;
    const all = new Float32Array(chunks.reduce((n, c) => n + c.length, 0));
    let o = 0;
    for (const c of chunks) {
      all.set(c, o);
      o += c.length;
    }
    void this.send(resample(all, rate), this.startedAt);
  }

  private async send(samples: Float32Array, at: number): Promise<void> {
    const session = this.deps.sessionId();
    // A backlog means the server is busy; drop rather than pile up (and keep the browser's
    // few connections to the server free for everything else).
    if (!session || this.inFlight >= 2) return;
    this.inFlight++;
    this.sent++;
    try {
      const res = await fetch(api('speech'), {
        method: 'POST',
        headers: {
          'content-type': 'application/octet-stream',
          'x-clinspace-session': session,
          'x-clinspace-at': String(at),
        },
        body: samples.buffer as ArrayBuffer,
      });
      if (res.status === 503) {
        // Switched off on the server: stop listening for the rest of the visit.
        this.dispose();
        return;
      }
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(body.error ?? `speech ${res.status}`);
    } catch (err) {
      this.lastError = (err as Error).message;
    } finally {
      this.inFlight--;
    }
  }

  dispose(): void {
    this.stopped = true;
    this.detach();
  }

  private detach(): void {
    this.chunks = [];
    this.preroll = [];
    this.detector.reset();
    if (this.processor) this.processor.onaudioprocess = null;
    this.source?.disconnect();
    this.processor?.disconnect();
    this.sink?.disconnect();
    this.source = undefined;
    this.processor = undefined;
    this.sink = undefined;
  }
}
